<template>
  <main class="max-w-screen-xl pl-4 py-4 mx-auto flex">
    <SkipToMain href="#courses-list" />
    <SidebarWrapper
      :open="filterMenuOpened"
      class="filter-menu sidebar"
      @close="closeFilterMenu"
    >
      <template #sidebar-heading>
        <div>
          <div class="flex items-start">
            <h1 class="text-xl flex-1 mr-2 heading-1">Courses</h1>
            <button
              type="button"
              aria-label="Close"
              class="md:hidden"
              @click="closeFilterMenu"
            >
              <BaseIcon icon="times" />
            </button>
          </div>
          <button
            v-if="isFiltering || searchQuery"
            type="button"
            class="mb-2 bg-theme-navbar-background color-theme-navbar-link hover:opacity-80 rounded-full px-2 py-1 transition ease-out duration-200  pill-label text-xs"
            @click="clearFilters"
          >
            <BaseIcon icon="times-circle" />
            Clear Filters
          </button>
        </div>
      </template>
      <div class="mb-8">
        <FilterInput
          v-model="searchQuery"
          placeholder="Type to search courses..."
          label="Search Courses"
          class="w-full"
          :config="{ type: 'search' }"
        >
          <template #iconStart>
            <BaseIcon
              icon="search"
              class="text-gray-700"
            />
          </template>
        </FilterInput>
      </div>
      <div
        class="overflow-y-auto"
        style="height:65vh"
      >
        <Filters
          :filters="filtersDataWithOptions"
          :values="filters"
          @input="filters = $event"
        />
      </div>
    </SidebarWrapper>
    <!-- TODO: Improve the layout to use fixed sidebar and free scroll content instead of local scrolling  -->
    <article
      id="courses-list"
      class="flex-1 px-4 overflow-y-auto"
      style="height:80vh"
    >
      <button
        type="button"
        class="bg-theme-navbar-background color-theme-navbar-link hover:opacity-80 rounded-full px-2 py-1 transition ease-out duration-200  pill-label text-xs lg:hidden mb-2"
        @click.stop="filterMenuOpened=true"
      >
        <BaseIcon icon="filter" />
        Filters
      </button>
      <div
        v-if="isLoading"
        class="p-20 flex justify-center"
      >
        <BaseSpinner />
      </div>
      <div v-else>
        <div class="flex items-center mb-2">
          <h1 class="text-2xl flex-1">Results ({{ total }})</h1>
          <div class="flex-1" />
          <label class="font-bold mr-2">Sort By</label>
          <FormSelect
            :value="sortBy || sortByOptionsWithEmptyValue[0]"
            :config="{ options: sortByOptionsWithEmptyValue }"
            :custom-label="decamelize"
            :hide-empty-value="true"
            @input="updateSortBy"
          />
        </div>
        <div v-if="coursesWithDepartment.length">
          <div class="rounded-lg overflow-hidden border border-gray-100">
            <button
              v-for="(course,i) in coursesWithDepartment"
              :key="i"
              :aria-expanded="expandedData === course ? 'true' : 'false'"
              type="button"
              class="course-button block w-full text-left p-6 border-b border-gray-100 cursor-pointer overflow-hidden hover:bg-gray-50"
              :class="{ 'bg-blue-50': expandedData && expandedData.id===course.id }"
              @click.stop="expandData(course)"
            >
              <h6
                v-if="course.code"
                class="pill-label text-xs mb-2 px-2 rounded bg-theme-base inline-block"
              >{{ course.code }}</h6>
              <h3>{{ courseNameWithFallbacks(course) || 'Untitled Course' }}</h3>
              <p>{{ _truncate(course.description || 'No Description', { separator: '...', length: 150 }) }}</p>
            </button>
          </div>
          <BasePagination
            v-if="total > pageSize"
            class="mt-4"
            :current-page="currentPage"
            :total-pages="totalPages"
            @page-change="updatePage"
          />
        </div>
        <EmptyState
          v-else
          label="No Courses Found"
          class="flex-1 mt-10"
        />
      </div>
    </article>
    <SidebarWrapper
      :open="!!expandedData"
      position="right"
      class="expand-sheet sidebar"
      @close="closeExpandedData"
    >
      <div
        v-if="expandedData"
        class="flex flex-col"
        style="height:96vh"
        :aria-hidden="!expandedData"
      >
        <FocusLock>
          <div class="flex items-start mb-2">
            <h1 class="text-xl flex-1 mr-2 heading-1">Course Details</h1>
            <button
              type="button"
              aria-label="Close"
              @click="closeExpandedData"
            >
              <BaseIcon icon="times" />
            </button>
          </div>
          <div>
            <h6
              v-if="expandedData.code"
              class="pill-label text-xs mb-2 px-2 rounded bg-theme-base inline-block"
            >{{ expandedData.code }}</h6>
          </div>
          <h1 class="text-2xl font-bold mb-4">{{ expandedData.name || 'Untitled Course' }}</h1>
          <div class="flex-1 overflow-y-auto content-height">
            <ResourcePreview
              v-if="configurableResourcePreview"
              class="rounded bg-white border border-gray-300"
              :value="expandedData"
              :template="coursePreviewTemplate"
            />
            <div v-else>
              <p>
                {{ expandedData.description || '...' }}
              </p>
              <div class="border rounded mt-4 bg-white">
                <table class="table-auto w-full">
                  <tbody>
                    <tr class="border-b">
                      <th class="py-2 info-table info-table--label px-4 text-left">Department</th>
                      <td class="py-2 info-table px-4">{{ departmentsList }}</td>
                    </tr>
                    <tr class="border-b">
                      <th class="py-2 info-table info-table--label px-4 text-left">Units</th>
                      <td class="py-2 info-table px-4">{{ creditUnits }}</td>
                    </tr>
                    <tr
                      v-if="!hideCareerSection"
                      class="border-b"
                    >
                      <th class="py-2 info-table info-table--label px-4 text-left">Career</th>
                      <td class="py-2 info-table px-4">{{ expandedData.career || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
                <CourseSchedule
                  v-if="sectionSchedulingRedesign"
                  :document-value="expandedData"
                  :displayed-in-table="true"
                />
              </div>
            </div>
          </div>
          <div class="bg-gray-50 pt-6">
            <button
              class="view-course w-full rounded-md flex items-center py-4 px-6 bg-theme-base pill-label transform hover:scale-95 transition-all duration-300 ease-in-out"
              @click="$router.push(getCourseUrl(expandedData))"
            >
              <span class="flex-1">View Course</span>
              <BaseIcon
                icon="long-arrow-alt-right"
                class="ml-2"
              />
            </button>
          </div>
        </FocusLock>
      </div>
    </SidebarWrapper>
  </main>
</template>

<script>
import { toRef, computed, watch, useStore } from '@nuxtjs/composition-api'
import { mapGetters } from 'vuex'
import _get from 'lodash/get'
import _truncate from 'lodash/truncate'
import { mixin as clickaway } from 'vue-clickaway'
import FocusLock from 'vue-focus-lock'
import {
  fillFiltersOptionsFromQuestions,
  mergeResourceFieldColumns,
} from '@/utils/filters'
import FormSelect from '@/components/Fields/FormSelect'
import SidebarWrapper from '~contemporaryTheme/components/SidebarWrapper'
import FilterInput from '~contemporaryTheme/components/Base/FilterInput'
import Filters from '~contemporaryTheme/components/Base/Filters'
import EmptyState from '~contemporaryTheme/components/Base/EmptyState'
import SkipToMain from '@/components/SkipToMain'
import useCourses from '@/composables/useCourses'
import useFilters from '@/composables/useFilters'
import ResourcePreview from '@/components/ResourcePreview'
import CourseSchedule from '@/components/Fields/CourseSchedule'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  CONFIGURABLE_RESOURCE_PREVIEW,
  SECTION_SCHEDULING_REDESIGN,
} from '@/constants/featureFlags'
import { courseNameWithFallbacks } from '@/utils/entityNames'
import { decamelize } from '@/utils/generalUtils'

export default {
  setup(props, { root }) {
    const {
      configurableResourcePreview,
      sectionSchedulingRedesign,
    } = useFeatureFlags({
      configurableResourcePreview: CONFIGURABLE_RESOURCE_PREVIEW,
      sectionSchedulingRedesign: SECTION_SCHEDULING_REDESIGN,
    })
    const store = useStore()
    const coursePreviewTemplate = computed(
      () => store.state.settings.coursePreviewTemplate
    )
    const additionalColumns = Object.values(
      coursePreviewTemplate.value.questions
    ).map((q) => {
      if (q.custom) return `customFields.${q.dataKey}`
      else return q.dataKey
    })

    const { filtersPayload, filtersData, filtersDefaultValues } = useFilters(
      root,
      {
        type: 'courses',
        prebuiltFilters: toRef(props, 'filterFields'),
      }
    )

    const baseColumns = [
      'displayName',
      'department',
      'description',
      'name',
      'courseNumber',
      'subjectCode',
      'code',
      'courseGroupId',
      'credits.creditHours',
      'career',
    ]
    const columns = mergeResourceFieldColumns(baseColumns, additionalColumns)

    const {
      isLoading,
      search,
      fetchData,
      fetchQuestion,
      coursesWithDepartment,
      filters,
      searchQuery,
      total,
      totalPages,
      currentPage,
      updatePage,
      updateQuery,
      pageSize,
      getCourseUrl,
      courseQuestions,
      sortByOptions,
      sortBy,
    } = useCourses(root, {
      filtersPayload,
      columns,
    })

    function updateSortBy(value) {
      const updatedValue = value === '-' ? '' : value

      sortBy.value = updatedValue
      updateQuery({ sortBy: updatedValue })
    }

    function clearFilters() {
      filters.value = {}
      searchQuery.value = null
    }

    const filtersDataWithOptions = computed(() => {
      return fillFiltersOptionsFromQuestions(
        filtersData.value,
        courseQuestions.value
      )
    })

    watch(
      filtersDefaultValues,
      () => {
        if (Object.values(filtersDefaultValues).length) {
          filters.value = { ...filters.value, ...filtersDefaultValues.value }
        }
      },
      { immediate: true }
    )

    return {
      isLoading,
      search,
      fetchData,
      fetchQuestion,
      coursesWithDepartment,
      filters,
      searchQuery,
      total,
      totalPages,
      currentPage,
      updatePage,
      pageSize,
      getCourseUrl,
      _truncate,
      _get,
      clearFilters,
      coursePreviewTemplate,
      questions: courseQuestions,
      filtersDataWithOptions,
      configurableResourcePreview,
      courseNameWithFallbacks,
      updateSortBy,
      sortBy,
      sortByOptions,
      sectionSchedulingRedesign,
    }
  },
  components: {
    FilterInput,
    Filters,
    EmptyState,
    FocusLock,
    SkipToMain,
    SidebarWrapper,
    ResourcePreview,
    FormSelect,
    CourseSchedule,
  },
  mixins: [clickaway],
  props: {
    filterFields: {
      type: Array,
      default: () => [],
    },
    hideCareerSection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterMenuOpened: false,
      expandedData: null,
      showAlert: true,
    }
  },
  head() {
    return {
      title: `Courses | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Courses | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('settings', ['catalogWideMetaTitle']),
    departmentsList() {
      if (!this.expandedData.department && !this.expandedData.departments)
        return '-'

      if (this.expandedData.departments.length)
        return this.expandedData.departments
          .map((d) => d.displayName)
          .join(', ')

      if (this.expandedData.department)
        return this.expandedData.department.displayName || '-'

      return '-'
    },
    sortByOptionsWithEmptyValue() {
      return ['-', ...this.sortByOptions]
    },
    isFiltering() {
      return Object.values(this.filters).filter(
        (el) => (Array.isArray(el) ? el.length : el)
      ).length
    },
    creditUnits() {
      const min = _get(this.expandedData, 'credits.creditHours.min')
      const max = _get(this.expandedData, 'credits.creditHours.max')
      const value = _get(this.expandedData, 'credits.creditHours.value')

      if (min !== undefined || max !== undefined) {
        if (min === max || max === undefined) {
          return min
        }

        if (min === undefined && max !== undefined) {
          return max
        }

        if (min !== max && max !== undefined) {
          return `${min}-${max}`
        }
      }

      if (value !== undefined) {
        return value
      }

      return '-'
    },
  },
  watch: {
    filters: {
      deep: true,
      handler: 'search',
    },
    '$route.query': 'fetchData',
  },
  async created() {
    this.isLoading = true
    await this.fetchQuestion()
    this.fetchData()
  },
  methods: {
    expandData(data) {
      this.expandedData = data
    },
    closeFilterMenu() {
      this.filterMenuOpened = false
    },
    closeExpandedData() {
      this.expandedData = null
    },
    decamelize,
  },
}
</script>

<style scoped>
.course-button:focus {
  @apply bg-gray-50;
}

.sidebar.filter-menu {
  @apply bg-gray-100;

  width: 320px;
}
.sidebar.expand-sheet {
  @apply bg-gray-50;

  max-width: 500px;
}
.content-height {
  max-height: calc(96vh - 250px);
}

h1 {
  color: var(--h1HeaderFontColor, var(--text-gray-900));
  font-family: var(--h1HeaderFontTypeface, inherit);
  font-weight: var(--h1HeaderFontWeight, 700);
}
h3 {
  color: var(--h3HeaderFontColor, inherit);
  font-family: var(--h3HeaderFontTypeface, inherit);
  font-weight: var(--h3HeaderFontWeight, 700);
}
p {
  color: var(--textFontColor, var(--text-gray-500));
  font-family: var(--textFontTypeface, inherit);
  font-weight: var(--textFontWeight, inherit);
}
.pill-label {
  font-family: var(--interfaceFontTypeface, inherit);
  color: white;
  font-weight: var(--interfaceFontWeight, 700);
}
.info-label {
  font-family: var(--interfaceFontTypeface, inherit);
  font-weight: var(--interfaceFontWeight, inherit);
}
.info-table {
  color: var(--interfaceFontColor, inherit);
  font-weight: var(--interfaceFontWeight, inherit);
  font-family: var(--interfaceFontTypeface, inherit);
}
.info-table--label {
  font-weight: var(--interfaceFontWeight, 700);
}
@media only screen and (min-width: 1024px) {
  .sidebar.filter-menu {
    @apply top-auto left-auto shadow-none relative h-auto rounded-lg z-0;

    transform: translateX(0);
  }
}
</style>
